﻿import { CircleSlash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { WORKING_CONFIG } from '@/features/dashboard/config/statusConfig'
import { PowerChip } from '@/features/dashboard/components/StatusChip'
import type { DevicePowerState } from '@/features/wizard-page/utils/devicePower'
import type { DeviceTemplate } from '@/api/template.api'
import { cn } from '@asad/crane-ui'

export interface DevicePowerIndicatorProps {
  deviceKind: NonNullable<DeviceTemplate['deviceKind']>
  state: DevicePowerState
  className?: string
}

const powerPresentation = (state: Exclude<DevicePowerState, 'unconfigured'>) => WORKING_CONFIG[state]

/** Stały, read-only renderer wskaźnika zasilania urządzenia. Nie jest elementem
 * układu ani celem DnD; kreator może opakować go własnym przyciskiem wyboru,
 * aby edytować istniejące pola mapowania statusu w `DeviceTemplate`. */
const DevicePowerIndicator = ({ deviceKind, state, className }: DevicePowerIndicatorProps) => {
  const { t } = useTranslation()
  const label = t(deviceKind === 'hoist' ? 'device.hoistPowerSupply' : 'device.cranePowerSupply')
  const stateText = state === 'unconfigured' ? t('device.powerUnconfigured') : t(powerPresentation(state).labelKey)
  const presentation = state === 'unconfigured' ? null : powerPresentation(state)
  const Icon = presentation?.Icon ?? CircleSlash2

  return (
    <div
      role='status'
      aria-label={`${label}: ${stateText}`}
      aria-atomic='true'
      className={cn('flex min-h-28 items-center gap-3 rounded-crane-md bg-crane-card-inset p-3', className)}
    >
      <span
        className={cn(
          'flex size-crane-gauge shrink-0 items-center justify-center rounded-crane-md ring-1 ring-inset',
          presentation
            ? cn(presentation.bg, presentation.text, 'ring-current/30')
            : 'bg-crane-background text-crane-muted-foreground ring-crane-border',
        )}
        aria-hidden
      >
        <Icon className='size-9' />
      </span>
      <span className='min-w-0'>
        <span className='block break-words text-crane-2xs font-semibold uppercase tracking-crane-label text-crane-muted-foreground'>
          {label}
        </span>
        <span
          className={cn('mt-1 block text-crane-xl font-semibold', presentation?.text ?? 'text-crane-muted-foreground')}
        >
          {stateText}
        </span>
      </span>
    </div>
  )
}

export const DevicePowerChip = ({ state, className }: Pick<DevicePowerIndicatorProps, 'state' | 'className'>) => {
  const { t } = useTranslation()

  if (state !== 'unconfigured') {
    return <PowerChip isWorking={state === 'on' ? 1 : 0} className={className} />
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-crane-sm bg-crane-muted px-2.5 py-1 text-xs font-semibold text-crane-muted-foreground',
        className,
      )}
    >
      <CircleSlash2 className='size-3' aria-hidden />
      {t('device.powerUnconfigured')}
    </span>
  )
}

export default DevicePowerIndicator
